import type { FulfillmentOrderLine } from "../types";
import type { EntryParseBaseInput, EntryParseResult } from "./confirmationEntryTypes";
import { factors, unit, EntryParseError, parseEntryDates, parseEntrySegments } from "./confirmationEntryPrimitives";
import { calculateFulfillmentLineAmount, validateFulfillmentLineDraft } from "./fulfillmentOrderEditorService";

/** 本分类显式决定日期来源，日期范围不隐式参与计次数量。 */
function datePatch(input: EntryParseBaseInput, range = input.range): Partial<FulfillmentOrderLine> {
  return parseEntryDates(input.routeDays, range, "住宿", "control");
}

/** 本分类服务主体资格与结算主体分别保留。 */
function providerPatch(input: EntryParseBaseInput): Partial<FulfillmentOrderLine> {
  return { fulfillmentSupplierId: null, fulfillmentSupplierName: null, settlementSupplierId: input.settlement?.id ?? null, settlementSupplierName: input.settlement?.name ?? null };
}

/** 本分类明确资源类型和计价单位后校验完整行。 */
function basePatch(input: EntryParseBaseInput, name: string, quantity: string, count: string, price: string, quantityUnit: string, extra: Partial<FulfillmentOrderLine> = {}): Partial<FulfillmentOrderLine> {
  const amount = calculateFulfillmentLineAmount(quantity, count, price);
  const patch: Partial<FulfillmentOrderLine> = {
    resourceType: "住宿", itemNameSnapshot: name, itemSpecSnapshot: null,
    quantity, billingCount: count, unitPriceAmount: price, amount, unit: quantityUnit,
    pricingUnitType: "room_night",
    reviewStatus: "confirmed", sourceType: "manual", status: "active", ...datePatch(input), ...providerPatch(input), ...extra,
  };
  const errors = validateFulfillmentLineDraft({ ...patch, id: input.allowZeroQuantity ? "existing-preview" : "local-preview" } as FulfillmentOrderLine);
  if (errors.length) throw new Error(errors.join("；"));
  return patch;
}
/** 解析本类单段原文，保留本类资源和计费约束。 */
function parseSegment(input: EntryParseBaseInput, text: string): Partial<FulfillmentOrderLine>[] {
  const hotel = input.provider && "spuId" in input.provider ? input.provider : null;
  if (!hotel?.spuId || !hotel.spuName) throw new EntryParseError("尚未选择服务酒店，请先从上方服务酒店候选中选择真实酒店", "provider");
  const stayText = text.normalize("NFKC").trim().replace(/^#/, "@");
  // 规格名称保留空格和字母；全局候选没有酒店 ID，保存时仍绑定所选酒店。
  const selected = input.candidates
    .filter((item) => item.resourceType === "住宿" && item.level === "sku" && item.skuName?.trim() && (item.spuId == null || item.spuId === hotel.spuId))
    .map((item) => ({ item, prefix: `@${item.skuName!.normalize("NFKC").trim()}` }))
    .sort((a, b) => b.prefix.length - a.prefix.length)
    .find(({ prefix }) => stayText === prefix || (stayText.startsWith(prefix) && /^\s/.test(stayText.slice(prefix.length))));
  // 手写新规格由正式保存事务按酒店与名称解析/新增；输入预览不创建资源。
  const typed = stayText.match(/^@(.+?)\s+((?:\d+(?:\.\d+)?|待填)\s*(?:间|晚)?\s*[×xX*].*)$/u);
  const roomName = selected?.item.skuName || typed?.[1].trim();
  if (!roomName) throw new EntryParseError("请填写@房型和间数×晚数×每晚单价，例如@三人间 1×2×100；也可从房型候选中选择", "resource");
  const formula = selected ? stayText.slice(selected.prefix.length) : typed![2];
  if (!formula.trim()) throw new Error("尚未填写住宿公式，请在房型后填写间数×晚数×每晚单价，例如1间×1晚×100元或1×1×100");
  const p = factors(formula, ["间数", "晚数", "每晚单价"], input.allowZeroQuantity);
  if (p.length === 2 && ["间", "晚"].includes(p[0].unit)) throw new Error(`住宿缺少${p[0].unit === "间" ? "晚数" : "间数"}，请填写完整的间数×晚数×每晚单价，例如1间×1晚×100元`);
  if (p.length !== 3) throw new Error(`住宿公式只有${p.length}项，间数、晚数和每晚单价需各填写一项，例如1间×1晚×100元或1×1×100`);
  unit(p[0], ["间"]); unit(p[1], ["晚"]);
  return [basePatch(input, hotel.spuName, p[0].value, p[1].value, p[2].value, "间", { itemSpecSnapshot: roomName, resourceSelection: { kind: "stay", staySpuId: hotel.spuId, skuName: roomName } })];
}

/** 独立解析住宿文本，保留本类日期、计费和资源规则。 */
export function parseStayEntry(input: EntryParseBaseInput): EntryParseResult {
  return parseEntrySegments(input.text, text => parseSegment(input, text), patch => [...(!patch.settlementSupplierId ? ["结算主体待设置"] : [])]);
}
